import { Box, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import ButtonLoader from "../../common/ButtonLoader/ButtonLoader";
import {
  CommonButton,
  CustomRequiredInputLabel,
} from "../../common/StyledComponents/StyledFields";
import { groupingStyles } from "../../ExpenseVoucher/ExpenseRequest/ExpenseRequest";
import CustomDatePickerField from "../../common/DatePicker/DatePicker";
import { useFormik } from "formik";
import {
  apiRequest,
  calculateOffsetDate,
  catchErrorMessageHandler,
  displayDateFormat,
  getDatesBetween,
} from "../../../infrastructure/backendService";
import * as Yup from "yup";
import apiPath from "../../../config/apiPaths.json";
import toast from "react-hot-toast";
import ErrorPopup from "../../common/ErrorPopup";
import WeeklyScheduleDialog from "../WeeklyScheduleDialog/WeeklyScheduleDialog";

interface priestAvailabilityScheduleProps {
  isViewPriestSchedule?: boolean;
  isHallSchedule?: boolean;
}

const PriestAvailabilitySchedule = (props: priestAvailabilityScheduleProps) => {
  const { isViewPriestSchedule, isHallSchedule } = props;
  const currentDate = new Date();

  const [isBtnLoading, setIsButtonLoading] = useState(false);
  const [priestsWeeklySchedule, setPriestsWeeklySchedule] = useState<any>(null);
  const [isAPICalled, setIsAPICalled] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [dateAndDays, setDayAndDays] = useState<any>(
    getDatesBetween(currentDate, calculateOffsetDate(currentDate))
  );
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState<any>(null);
  const [isExcelLoading, setIsExcelLoading] = useState(false);

  const initialValues = {
    date: displayDateFormat(currentDate),
  };

  const validationSchema = Yup.object().shape({
    date: Yup.string().required("From date is required"),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit(values) {
      setIsButtonLoading(true);
      scheduleApiCall({
        startDate: new Date(values?.date),
        endDate: calculateOffsetDate(new Date(values?.date)),
      });
    },
    enableReinitialize: true,
  });

  useEffect(() => {
    formik.resetForm();
  }, [isViewPriestSchedule, isHallSchedule])

  const [schedulePeriod, setSchedulePeriod] = useState({
    startDate: new Date(),
    endDate: calculateOffsetDate(new Date()),
  });

  const sortSchedulesByTime = (schedules: any[]): any[] => {
    return schedules?.sort((a, b) => {
      const timeA = new Date(`1970/01/01 ${a?.startTime}`).getTime();
      const timeB = new Date(`1970/01/01 ${b?.startTime}`).getTime();
      return timeA - timeB;
    });
  };

  const organizeSchedules = (schedules: any[], dayWithDate: any[]): any[] => {
    const result: any[] = dayWithDate?.map((dateInfo) => {
      const schedulesForDate = schedules?.filter(
        (schedule) => schedule?.date === dateInfo?.date
      );
      return {
        date: dateInfo.date,
        schedules:
          schedulesForDate?.length > 0
            ? sortSchedulesByTime(schedulesForDate)
            : null,
      };
    });

    return result;
  };

  const scheduleApiCall = async (values: any) => {
    await apiRequest({
      method: "GET",
      endpoint: apiPath.schedules,
      params: {
        scheduleType: !isHallSchedule ? "PRIEST" : "HALL",
        schedulesForDay: false,
        startDate: displayDateFormat(values?.startDate),
        endDate: displayDateFormat(values?.endDate),
        weeklySchedule: true,
      },
      includeToken: true,
    })
      .then((res) => {
        const organizedSchedules = res?.data?.map((priestSchedule: any) => ({
          ...priestSchedule,
          schedulesByDate: organizeSchedules(
            priestSchedule?.schedulesByDate,
            getDatesBetween(values?.startDate, values?.endDate)
          ),
        }));
        setPeriod({...values})
        setPriestsWeeklySchedule(organizedSchedules);
        setIsAPICalled(true);
        setIsButtonLoading(false);
        setIsPageLoading(false);
        setOpen(true);
      })
      .catch((err) => {
        setOpen(false);
        setPriestsWeeklySchedule([]);
        setIsAPICalled(true);
        setIsButtonLoading(false);
        setIsPageLoading(false);
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />
        );
      });
  };

  const handleExportToExcel = async () => {
    const { startDate, endDate } = period;
    setIsExcelLoading(true);
    apiRequest({
      method: "GET",
      endpoint: `${apiPath.schedules}/excel`,
      params: {
        scheduleType: !isHallSchedule ? "PRIEST" : "HALL",
        schedulesForDay: false,
        startDate: displayDateFormat(new Date(startDate)),
        endDate: displayDateFormat(endDate),
        weeklySchedule: true,
      },
      includeToken: true,
      responseType: "arraybuffer",
    })
      .then((res) => {
        const blob = new Blob([res?.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const link = document.createElement("a");
        link.download = `Weekly_${
          !isHallSchedule ? "Priest" : "Hall"
        }_Schedule_${displayDateFormat(startDate)}_to_${displayDateFormat(
          endDate
        )}.xlsx`;
        link.href = window.URL.createObjectURL(blob);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setIsExcelLoading(false);
      })
      .catch((err) => {
        setIsExcelLoading(false);
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />
        );
      });
  };

  const handleChange = (field: string, value: any) => {
    formik.setFieldValue(field, value);
    setSchedulePeriod({
      startDate: new Date(value),
      endDate: calculateOffsetDate(new Date(value)),
    });
    setDayAndDays(
      getDatesBetween(new Date(value), calculateOffsetDate(new Date(value)))
    );
  };

  const handleClose = () => {
    setOpen(false);
    setPriestsWeeklySchedule([]);
    setIsAPICalled(false);
    setDayAndDays(
      getDatesBetween(
        new Date(formik?.values.date),
        calculateOffsetDate(new Date(formik?.values.date))
      )
    );
    setSchedulePeriod({
      startDate: new Date(formik?.values.date),
      endDate: calculateOffsetDate(new Date(formik?.values.date)),
    });
  };

  const handlePreviousAndNextWeek = (isPrevious = false) => {
    let startDate = schedulePeriod?.startDate;
    let endDate = schedulePeriod.endDate;
    if (isPrevious) {
      endDate = calculateOffsetDate(startDate, -1);
      startDate = calculateOffsetDate(startDate, -7);
    } else {
      startDate = calculateOffsetDate(endDate, 1);
      endDate = calculateOffsetDate(endDate, 7);
    }
    setSchedulePeriod({ startDate, endDate });
    setDayAndDays(getDatesBetween(startDate, endDate));
    scheduleApiCall({ startDate, endDate });
    setIsPageLoading(true);
  };

  const renderFieldsComponent = () => {
    return (
      <Box className="common_box_shadow" sx={{ mt: "20px" }}>
        {isViewPriestSchedule && (
          <Typography
            className="sub_heading"
            sx={{ fontSize: "16px !important", m: "0px 0px 10px 0 !important" }}
          >
            {!isHallSchedule ? "Priest": "Hall"} Schedule (Weekly)
          </Typography>
        )}
        <Box sx={groupingStyles.groupedFieldsContainer}>
          <Box sx={groupingStyles.fieldContainer}>
            {CustomRequiredInputLabel("Start Date", true)}
            <CustomDatePickerField
              date={formik.values.date}
              handleDateChange={handleChange}
              errorMessage={formik.touched.date ? formik.errors.date : ""}
              name="date"
            />
          </Box>
        </Box>
        <CommonButton
          onClick={() => {
            !isBtnLoading && formik.handleSubmit();
          }}
          sx={{
            mt: `${formik.touched.date && formik.errors.date ? "20px" : "5px"}`,
          }}
        >
          <ButtonLoader
            isBtnLoading={isBtnLoading}
            btnText={isViewPriestSchedule ? "Weekly Schedule" : "Submit"}
          />
        </CommonButton>
      </Box>
    );
  };

  return (
    <>
      {!isViewPriestSchedule && (
        <Typography className="sub_heading">Priest Weekly Schedule</Typography>
      )}
      {renderFieldsComponent()}
      {isAPICalled && (
        <>
          {priestsWeeklySchedule?.length > 0 ? (
            <WeeklyScheduleDialog
              handleClose={handleClose}
              open={open}
              isPageLoading={isPageLoading}
              schedulePeriod={schedulePeriod}
              priestsWeeklySchedule={priestsWeeklySchedule}
              dateAndDays={dateAndDays}
              handlePreviousAndNextWeek={handlePreviousAndNextWeek}
              isViewPriestSchedule={isViewPriestSchedule}
              isHallSchedule={isHallSchedule}
              handleExportToExcel={handleExportToExcel}
              isExcelLoading={isExcelLoading}
            />
          ) : (
            <Typography
              className="sub_heading"
              sx={{ fontSize: "18px !important" }}
            >
              There are no schedules within the given date range.
            </Typography>
          )}
        </>
      )}
    </>
  );
};

export default PriestAvailabilitySchedule;
